import { Params, Router } from '@angular/router';

import { environment } from '../../environments/environment';
import { Constants } from '@helper/constants';
import { LOCALES } from '../../environments/locales';

export class Links {
  static getURL(link: string): [string, Params | undefined] {
    if (!link.includes(location.origin)) {
      link = `${location.origin}${link.startsWith('/') ? link : `/${link}`}`;
    }
    const url = new URL(link);
    if (url.searchParams.size) {
      const params: Params = {};
      for (const [key, value] of url.searchParams) {
        params[key] = value;
      }
      return [url.pathname, params];
    }
    return [url.pathname, undefined];
  }

  static extractUrlToRouterLink(link: string | undefined): [string, { [key: string]: string }] {
    if (!link) {
      return ['', {}];
    }
    if (!link.startsWith('http')) {
      return ['', {}];
    }
    const url = new URL(link);
    if (url.hostname.endsWith(Constants.DOMAIN) || url.hostname.endsWith(Constants.WEBVIEW)) {
      // internal path
      if (url.searchParams.has('url')) {
        return [url.searchParams.get('url') ?? '', {}];
      } else {
        return [url.pathname, Object.fromEntries(url.searchParams.entries())];
      }
    }
    return [url.pathname, Object.fromEntries(url.searchParams.entries())];
  }

  static extractUrl(link: string | undefined): string | null {
    if (!link) {
      return null;
    }
    if (!link.startsWith('http')) {
      return link;
    }
    const url = new URL(link);
    if (url.hostname.endsWith(Constants.DOMAIN) || url.hostname.endsWith(Constants.WEBVIEW)) {
      // internal path
      if (url.pathname.includes('legacy/') && link.startsWith('http')) {
        return link.replace('www', 'legacy');
      } else if (url.searchParams.has('url')) {
        return url.searchParams.get('url') ?? '';
      } else {
        return `${url.pathname}${url.search}`;
      }
    }
    return link;
  }

  static handleLink(router: Router, link: string, openWindow = false): void {
    if (!link) {
      return;
    }
    const url = Links.extractUrl(link);
    if (!url) {
      return;
    }
    if (!url.startsWith('http')) {
      // internal path
      if (openWindow) {
        Links.openInNewWindow(url);
      } else {
        router.navigateByUrl(url);
      }
      return;
    }
    if (openWindow || !url.includes(Constants.DOMAIN)) {
      Links.openInNewWindow(url);
    } else {
      router.navigateByUrl(url);
    }
  }

  static openInNewWindow(link: string): void {
    if (!link) {
      return;
    }
    if (link.startsWith('http')) {
      const url = new URL(link);
      if (url.hostname.endsWith(Constants.DOMAIN) && !environment.legacyEndPointUrl.includes(url.hostname)) {
        // internal path
        window.open(`${environment.endPointUrl}${Links.appendLocale(url.pathname)}`);
      } else {
        window.open(link);
      }
    } else {
      window.open(`${environment.endPointUrl}${Links.appendLocale(link)}`);
    }
  }

  static getSiteLocale(): string | undefined {
    try {
      const locale = window.location.pathname.split('/')[1];
      return LOCALES.includes(locale) ? locale : undefined;
    } catch {
      return undefined;
    }
  }

  static appendLocale(link: string): string {
    if (!link.startsWith('/')) {
      link = `/${link}`;
    }
    const locale = Links.getSiteLocale();
    if (locale) {
      link = `/${locale}${link}`;
    }
    return link;
  }

  static removeLocale(link: string): string {
    if (!link.startsWith('/')) {
      link = `/${link}`;
    }
    const locale = Links.getSiteLocale();
    if (locale) {
      link = link.replaceAll(`/${locale}`, '');
    }
    return link;
  }

  static endPointUrl(doc: Document): string {
    if (!location.hostname.includes(Constants.DOMAIN)) {
      return environment.endPointUrl;
    }
    return `${doc.location.protocol}//${doc.location.hostname}`;
  }

  static trackingId(params: Params): string {
    const param = params[Constants.AD_TRACKING_ID];
    return (Array.isArray(param) ? param[0] : param)?.substring(0, Constants.AD_TRACKING_ID_LENGTH) || '';
  }
}
